import { BadgePercent, CheckCircle2, ShieldCheck, Target, UsersRound } from "lucide-react";
import type { PageLocale } from "@/utils/pageLocale";

const CALL_OUTCOMES = [
  {
    icon: Target,
    title: "Choose the right workflow",
    description:
      "Focus on the work most worth automating or improving, based on what you already do.",
  },
  {
    icon: ShieldCheck,
    title: "Find the blocker before you build or improve",
    description:
      "Identify the missing context, quality check, risk, or integration that could keep the agent from being useful.",
  },
  {
    icon: CheckCircle2,
    title: "Agree on a useful next step",
    description:
      "Discuss where to start and whether hands-on support would help you make progress.",
  },
];

/** Outcomes shown when the visitor arrived from the OpenAI partner page's
    pricing CTAs, so the call reads as a partner-pricing intake instead of
    the AI Agent Program roadmap. */
const PARTNER_PRICING_OUTCOMES = [
  {
    icon: BadgePercent,
    title: "Get a partner-pricing quote",
    description:
      "Walk through what your team pays OpenAI today (or would pay direct) and see the discounted partner price for the same plan.",
  },
  {
    icon: UsersRound,
    title: "Confirm the right plan and seat count",
    description:
      "Tell us how many people need access and what they need to do with it. We'll confirm eligibility and match the plan to the work.",
  },
  {
    icon: CheckCircle2,
    title: "Leave with an adoption plan",
    description:
      "Seats are only useful if people use them. You'll leave with the first three steps to get your team productive on the tools.",
  },
];

const en = {
  organization: {
    seoTitle: "Discuss Your Organization’s AI Priorities | Tech Leaders",
    seoDescription: "Book a free 15-minute conversation about your organization’s AI priorities, team training, workflow implementation, and adoption support.",
    badge: "Free 15-minute organization discovery phone call",
    headline: "Let’s Talk About AI in Your Organization",
    bookingDescription: "You’ll speak with Todd by phone for 15 minutes. Bring your organization’s priorities, questions, and a picture of how your team uses AI today.",
    calendarTitle: "Schedule your organization’s AI discovery call with Todd Larsen",
    intro: "Whether your teams are getting started or already building with AI, we’ll discuss what you want to improve, where adoption is getting stuck, and how training or implementation support could help.",
    hostNote: "I’ll ask about your people, priorities, and current AI work. We’ll discuss where Tech Leaders could help and what a useful next step would look like for your organization.",
    outcomes: [
      { icon: Target, title: "Clarify your organization’s priorities", description: "Discuss the work you want to improve and the outcomes that matter to your leadership team." },
      { icon: UsersRound, title: "Understand where your people are today", description: "Talk through current tools, team experience, and the obstacles to putting AI into everyday use." },
      { icon: CheckCircle2, title: "Discuss the right next step", description: "Explore whether training, workflow implementation, or ongoing coaching fits your organization’s needs." },
    ],
  },
  "partner": {
    outcomes: PARTNER_PRICING_OUTCOMES,
    "seoTitle": "Get Partner Pricing on OpenAI Plans | Tech Leaders",
    "seoDescription": "Book a free 15-minute call to get a discounted partner-pricing quote on OpenAI business plans through Tech Leaders, an OpenAI SMB Channel Partner, plus a plan to get your team using them.",
    "badge": "Free 15-minute partner pricing phone call",
    "headline": "Get Partner Pricing on OpenAI Plans",
    "bookingDescription": "You'll speak with Todd by phone for 15 minutes. Bring your seat count; we'll bring the partner-pricing quote.",
    "calendarTitle": "Schedule your partner pricing call with Todd Larsen",
    "intro": "As an OpenAI SMB Channel Partner, Tech Leaders can offer your business discounted pricing on OpenAI plans. Bring your seat count and what your team needs to do. We'll confirm eligibility, quote partner pricing, and map the first adoption steps.",
    "hostNote": "I'll walk you through what partner pricing looks like for your team and which OpenAI plan fits. If buying through Tech Leaders makes sense, I'll send a quote after the call. If it doesn't, you'll still leave with a clear next step."
  },
  "workflow": {
    outcomes: CALL_OUTCOMES,
    "seoTitle": "Map Your Next Useful AI Workflow | Tech Leaders",
    "seoDescription": "Book a free 15-minute call to map one workflow. Choose what to automate or improve, identify the biggest blocker, and discuss a useful next step.",
    "badge": "Free 15-minute workflow phone call",
    "headline": "Map Your Next Useful AI Workflow",
    "bookingDescription": "You'll speak with Todd by phone for 15 minutes. Bring one workflow, recurring task, or agent you want to improve; we'll map what to automate or improve next.",
    "calendarTitle": "Schedule your AI workflow roadmap with Todd Larsen",
    "intro": "Already using agents or starting fresh? Bring one workflow from the library, one recurring task that keeps eating your time, or one agent you want to improve. We'll discuss the main blocker, a useful next step, and whether AI 30 fits the work.",
    "hostNote": "I'll help you narrow the idea to one useful workflow. If AI 30 fits, I'll explain how we'd help. If it doesn't, you'll still leave with a clear next step."
  },
  "workflowLabel": "The workflow you brought",
  "founder": "Founder, Tech Leaders",
  "videoTitle": "What it is like to work with Tech Leaders",
  "chooseTime": "Choose a time that works",
  "calendarNotLoading": "Calendar not loading?",
  "openCalendar": "Open it in a new tab",
  "noTimes": "Can't find a time?",
  "emailTodd": "Email Todd",
  "languageNote": "",
  "videoLabel": "",
  "keywords": [
    "AI agent strategy",
    "AI workflow roadmap",
    "AI implementation call",
    "AI agent program",
    "business AI automation"
  ]
};

const pt: typeof en = {
  organization: {
    seoTitle: "Converse sobre as prioridades de IA da sua organização | Tech Leaders",
    seoDescription: "Agende uma conversa gratuita de 15 minutos sobre prioridades de IA, capacitação da equipe, implementação de processos e apoio à adoção.",
    badge: "Conversa inicial gratuita de 15 minutos para organizações",
    headline: "Vamos conversar sobre IA na sua organização",
    bookingDescription: "Você conversará com Todd por telefone durante 15 minutos. Traga as prioridades, dúvidas e um panorama de como sua equipe usa IA hoje.",
    calendarTitle: "Agende uma conversa inicial sobre IA com Todd Larsen",
    intro: "Se suas equipes estão começando ou já trabalham com IA, vamos discutir o que você quer melhorar, os obstáculos à adoção e como a capacitação ou o apoio à implementação podem ajudar.",
    hostNote: "Vou perguntar sobre sua equipe, suas prioridades e o trabalho atual com IA. Conversaremos sobre como a Tech Leaders pode ajudar e qual próximo passo faz sentido para sua organização.",
    outcomes: [
      { icon: Target, title: "Esclareça as prioridades da organização", description: "Converse sobre o trabalho que deseja melhorar e os resultados importantes para sua liderança." },
      { icon: UsersRound, title: "Entenda o momento da equipe", description: "Discuta as ferramentas atuais, a experiência das pessoas e os obstáculos ao uso de IA no dia a dia." },
      { icon: CheckCircle2, title: "Discuta o próximo passo", description: "Explore se capacitação, implementação de processos ou acompanhamento contínuo atendem às necessidades da organização." },
    ],
  },
  "partner": {
    outcomes: [
      { ...PARTNER_PRICING_OUTCOMES[0], title: "Receba uma cotação de parceiro", description: "Converse sobre o que sua equipe paga à OpenAI hoje, ou pagaria na contratação direta, e conheça as condições de parceiro para o mesmo plano." },
      { ...PARTNER_PRICING_OUTCOMES[1], title: "Defina o plano e a quantidade de licenças", description: "Conte quantas pessoas precisam de acesso e o que precisam fazer. Vamos confirmar a elegibilidade e indicar o plano adequado ao trabalho." },
      { ...PARTNER_PRICING_OUTCOMES[2], title: "Saia com um plano de adoção", description: "As licenças só geram valor quando são usadas. Você sairá com os três primeiros passos para sua equipe começar a trabalhar com as ferramentas." },
    ],
    "seoTitle": "Solicite uma cotação de parceiro OpenAI | Tech Leaders",
    "seoDescription": "Agende uma conversa gratuita de 15 minutos com a Tech Leaders para solicitar uma cotação de parceiro em planos empresariais elegíveis da OpenAI e definir os primeiros passos de adoção.",
    "badge": "Conversa gratuita de 15 minutos sobre condições de parceiro",
    "headline": "Solicite uma cotação de parceiro para planos OpenAI",
    "bookingDescription": "Você conversará com Todd por telefone durante 15 minutos. Informe quantas licenças precisa para conversarmos sobre a cotação de parceiro.",
    "calendarTitle": "Agende sua conversa sobre condições de parceiro com Todd Larsen",
    "intro": "Como participante do programa OpenAI SMB Channel Partner, a Tech Leaders pode oferecer condições comerciais diferenciadas em planos OpenAI. Conte quantas pessoas precisam de acesso e o que sua equipe precisa fazer. Confirmamos a elegibilidade, apresentamos uma cotação de parceiro e definimos os primeiros passos de adoção.",
    "hostNote": "Vou explicar as condições de parceiro para sua equipe e ajudar a identificar o plano OpenAI adequado. Se a contratação pela Tech Leaders fizer sentido, enviarei uma cotação após a conversa. Em qualquer caso, você sairá com um próximo passo claro."
  },
  "workflow": {
    outcomes: [
      { ...CALL_OUTCOMES[0], title: "Escolha o primeiro processo de trabalho", description: "Concentre-se no trabalho que mais vale a pena automatizar, considerando o valor para sua equipe." },
      { ...CALL_OUTCOMES[1], title: "Identifique o obstáculo antes de começar", description: "Descubra qual contexto, verificação de qualidade, risco ou integração pode impedir que o agente seja útil." },
      { ...CALL_OUTCOMES[2], title: "Defina um próximo passo útil", description: "Converse sobre por onde começar e se o apoio prático pode ajudar você a avançar." },
    ],
    "seoTitle": "Planeje seu primeiro agente de IA | Tech Leaders",
    "seoDescription": "Traga um processo de trabalho recorrente. Em uma conversa telefônica de 15 minutos, discuta o principal obstáculo, um próximo passo útil e se o AI 30 atende ao que você precisa.",
    "badge": "Conversa gratuita de 15 minutos sobre seu processo de trabalho",
    "headline": "Planeje seu primeiro agente de IA",
    "bookingDescription": "Você conversará com Todd por telefone durante 15 minutos. Traga seu processo de trabalho para planejarmos o agente de IA.",
    "calendarTitle": "Agende sua conversa sobre processos de trabalho com IA com Todd Larsen",
    "intro": "Traga um processo da biblioteca ou uma tarefa recorrente que consome seu tempo. Vamos conversar sobre o principal obstáculo, um próximo passo útil e se o AI 30 atende ao que você precisa.",
    "hostNote": "Vou ajudar você a transformar sua ideia em um processo de trabalho útil. Se o AI 30 fizer sentido, explicarei como podemos ajudar. Em qualquer caso, você sairá com um próximo passo claro."
  },
  "workflowLabel": "O processo de trabalho que você trouxe",
  "founder": "Fundador da Tech Leaders",
  "videoTitle": "Como é trabalhar com a Tech Leaders (vídeo em inglês)",
  "chooseTime": "Escolha o melhor horário",
  "calendarNotLoading": "O calendário não carregou?",
  "openCalendar": "Abra em uma nova aba",
  "noTimes": "Não encontrou um horário?",
  "emailTodd": "Envie um e-mail para Todd",
  "languageNote": "A conversa com Todd será em inglês.",
  "videoLabel": "Vídeo em inglês",
  "keywords": [
    "parceiro OpenAI",
    "cotação OpenAI para empresas",
    "adoção de IA",
    "implementação de IA",
    "agentes de IA"
  ]
};

export const aiCallContent: Record<PageLocale, typeof en> = { en, "pt-BR": pt };
